import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { Room } from '@shared/types';
import { Header } from '../components/layout/Header';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';

// Logo size configuration
const LOGO_SIZES = {
  small: { height: 60, label: 'Small' },
  medium: { height: 80, label: 'Medium' },
  large: { height: 120, label: 'Large' },
  xl: { height: 160, label: 'Extra Large' }
} as const;

type LogoSize = keyof typeof LOGO_SIZES;

// Message configuration
const MESSAGE_SIZES = {
  small: { fontSize: '1.2rem', label: 'Small' },
  medium: { fontSize: '1.5rem', label: 'Medium' },
  large: { fontSize: '1.8rem', label: 'Large' }
} as const;

const MESSAGE_SPEEDS = {
  static: { duration: 0, label: 'Static (No Scroll)' },
  slow: { duration: 70, label: 'Slow' },
  medium: { duration: 40, label: 'Medium' },
  fast: { duration: 30, label: 'Fast' }
} as const;

type MessageSize = keyof typeof MESSAGE_SIZES;
type MessageSpeed = keyof typeof MESSAGE_SPEEDS;

export function TVDashboardSettings() {
  const { userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoSize, setLogoSize] = useState<LogoSize>('medium');
  const [messageText, setMessageText] = useState<string>('');
  const [messageSize, setMessageSize] = useState<MessageSize>('medium');
  const [messageSpeed, setMessageSpeed] = useState<MessageSpeed>('medium');
  const [showToast, setShowToast] = useState<boolean>(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState<Error | null>(null);
  const [tvTheme, setTvTheme] = useState<'light' | 'dark'>('dark');
  
  // Get the current room for this admin
  const currentRoomId = searchParams.get('roomId') || userProfile?.roomId;

  // Direct Firebase approach for rooms
  useEffect(() => {
    if (!db) {
      setRoomsError(new Error('Firebase not initialized'));
      setRoomsLoading(false);
      return;
    }

    const roomsRef = collection(db, 'rooms');
    
    const unsubscribe = onSnapshot(
      roomsRef,
      (snapshot) => {
        const roomsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Room[];
        setRooms(roomsData);
        setRoomsLoading(false);
      },
      (err) => {
        setRoomsError(err as Error);
        setRoomsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);
  
  const currentRoom = rooms?.find((room: Room) => room.id === currentRoomId);

  // Update logoSize state when room data loads
  React.useEffect(() => {
    if (currentRoom?.logoSize) {
      setLogoSize(currentRoom.logoSize);
    }
    if (currentRoom) {
      setMessageText(currentRoom.messageText || '');
      setMessageSize(currentRoom.messageSize || 'medium');
      setMessageSpeed(currentRoom.messageSpeed || 'medium');
      setTvTheme(currentRoom.tvTheme || 'dark');
    }
  }, [currentRoom?.logoSize, currentRoom?.messageText, currentRoom?.messageSize, currentRoom?.messageSpeed, currentRoom?.tvTheme]);

  const updateLogoSize = async (newSize: LogoSize) => {
    if (!currentRoomId) return;
    
    // Check authorization (admin/superadmin)
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to update logo size.');
      return;
    }
    
    // For regular admins, check if they have access to this specific room
    if (userProfile.role === 'admin' && userProfile.roomId !== currentRoomId) {
      setError('You do not have permission to update logo size for this room.');
      return;
    }
    
    try {
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        logoSize: newSize,
        updated_at: new Date()
      });
      
      setLogoSize(newSize);
      setSuccess('Logo size updated successfully!');
      setError(null);
    } catch (err) {
      setError('Failed to update logo size');
    }
  };

  const updateTheme = async (newTheme: 'light' | 'dark') => {
    if (!currentRoomId) return;
    
    // Check authorization (admin/superadmin)
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to update theme.');
      return;
    }
    
    // For regular admins, check if they have access to this specific room
    if (userProfile.role === 'admin' && userProfile.roomId !== currentRoomId) {
      setError('You do not have permission to update theme for this room.');
      return;
    }
    
    try {
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        tvTheme: newTheme,
        updated_at: new Date()
      });
      
      setTvTheme(newTheme);
      setSuccess('Theme updated successfully!');
      setError(null);
    } catch (err) {
      setError('Failed to update theme');
    }
  };

  const updateMessage = async () => {
    if (!currentRoomId) return;
    
    // Check authorization
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to update messages.');
      return;
    }
    
    if (userProfile.role === 'admin' && userProfile.roomId !== currentRoomId) {
      setError('You do not have permission to update messages for this room.');
      return;
    }
    
    try {
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        messageText: messageText.trim(),
        messageSize,
        messageSpeed,
        updated_at: new Date()
      });
      
      setSuccess('Message updated successfully!');
      setError(null);
      
      // Show toast notification
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      setError('Failed to update message');
    }
  };

  const clearMessage = async () => {
    if (!currentRoomId) return;
    
    // Check authorization
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to clear messages.');
      return;
    }
    
    try {
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        messageText: '',
        updated_at: new Date()
      });
      
      setMessageText('');
      setSuccess('Message cleared successfully!');
      setError(null);
    } catch (err) {
      setError('Failed to clear message');
    }
  };

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PNG or SVG file only.';
    }
    
    // Check file size (500KB max)
    if (file.size > 500000) {
      return 'File size must be less than 500KB.';
    }
    
    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const uploadLogo = async () => {
    if (!selectedFile || !currentRoomId) return;
    
    // Check authorization before upload
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to upload logos. Admin access required.');
      return;
    }
    
    // For regular admins, check if they have access to this specific room
    if (userProfile.role === 'admin' && userProfile.roomId !== currentRoomId) {
      setError('You do not have permission to upload logos for this room.');
      return;
    }
    
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get file extension
      const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
      if (!fileExt || !['png', 'svg'].includes(fileExt)) {
        throw new Error('Invalid file type');
      }
      
      // Create storage reference
      const storageRef = ref(storage, `rooms/${currentRoomId}/logo.${fileExt}`);
      
      // Delete old logo if it exists
      if (currentRoom?.logoUrl) {
        try {
          const oldLogoRef = ref(storage, currentRoom.logoUrl);
          await deleteObject(oldLogoRef);
        } catch (deleteError) {
          // Ignore delete errors (file might not exist)
        }
      }
      
      // Upload new logo
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update room document with new logo URL
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        logoUrl: downloadURL,
        logoSize: logoSize, // Set the current logo size
        updated_at: new Date()
      });
      
      setSuccess('Logo uploaded successfully!');
      
      // Clear preview and selected file
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!currentRoomId || !currentRoom?.logoUrl) return;
    
    // Check authorization before removal
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'superadmin')) {
      setError('You do not have permission to remove logos. Admin access required.');
      return;
    }
    
    // For regular admins, check if they have access to this specific room
    if (userProfile.role === 'admin' && userProfile.roomId !== currentRoomId) {
      setError('You do not have permission to remove logos for this room.');
      return;
    }
    
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Delete logo from storage
      const logoRef = ref(storage, currentRoom.logoUrl);
      await deleteObject(logoRef);
      
      // Update room document to remove logo URL
      const roomRef = doc(db, 'rooms', currentRoomId);
      await updateDoc(roomRef, {
        logoUrl: null,
        updated_at: new Date()
      });
      
      setSuccess('Logo removed successfully!');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove logo');
    } finally {
      setUploading(false);
    }
  };

  if (roomsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (roomsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">
            {roomsError?.message || 'Failed to load room data'}
          </p>
        </div>
      </div>
    );
  }

  if (!currentRoomId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Room Assigned</h1>
          <p className="text-gray-600">
            You haven't been assigned to a poker room yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile} />
        
        <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    TV Dashboard Settings
                  </h1>
                  <p className="mt-2 text-gray-600">
                    Customize the TV Dashboard display for {currentRoom?.name || 'your poker room'}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/admin')}
                  className="btn btn-secondary"
                >
                  Back to Admin Dashboard
                </button>
              </div>
            </div>

            {/* Logo Upload Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Room Logo</h2>
              
              {/* Upload Guidelines */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Logo Upload Guidelines</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• PNG or SVG format (transparent background preferred)</li>
                  <li>• Recommended size: 320 × 120 pixels (wide layout)</li>
                  <li>• Maximum file size: 500 KB</li>
                  <li>• SVG logos scale best on all screen sizes</li>
                </ul>
              </div>

              {/* Current Logo Display */}
              {currentRoom?.logoUrl && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Current Logo</h3>
                  <div className="flex items-center space-x-4">
                    <img
                      src={currentRoom.logoUrl}
                      alt="Current room logo"
                      className="h-16 object-contain border border-gray-200 rounded"
                    />
                    <button
                      onClick={removeLogo}
                      disabled={uploading}
                      className="btn btn-danger btn-sm"
                    >
                      Remove Logo
                    </button>
                  </div>
                </div>
              )}

              {/* File Upload Area */}
              <div className="mb-6">
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="space-y-2">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium text-blue-600 hover:text-blue-500">
                        Click to upload
                      </span>
                      {' '}or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">PNG or SVG up to 500KB</p>
                  </div>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.svg,image/png,image/svg+xml"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>

              {/* Preview */}
              {previewUrl && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                  <div className="flex items-center space-x-4">
                    <img
                      src={previewUrl}
                      alt="Logo preview"
                      className="h-16 object-contain border border-gray-200 rounded"
                    />
                    <div className="text-sm text-gray-600">
                      {selectedFile?.name} ({(selectedFile?.size || 0 / 1024).toFixed(1)} KB)
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={uploadLogo}
                  disabled={!selectedFile || uploading}
                  className="btn btn-primary"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Uploading...
                    </>
                  ) : (
                    'Upload Logo'
                  )}
                </button>
                
                {selectedFile && (
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
              
              {success && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">TV Dashboard Preview</h2>
              <p className="text-sm text-gray-600 mb-4">
                This is how your TV Dashboard will appear ({tvTheme === 'light' ? 'Light' : 'Dark'} mode):
              </p>
              
              {/* Mock TV Dashboard Header */}
              <div className={`px-8 py-6 border-b-4 rounded-lg ${
                tvTheme === 'light' 
                  ? 'bg-gray-50 border-blue-500' 
                  : 'bg-gradient-to-r from-blue-900 to-blue-800 border-blue-600'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className={`text-4xl font-bold tracking-wider ${
                      tvTheme === 'light' ? 'text-blue-600' : 'text-blue-200'
                    }`}>POKER</div>
                    <div className={`text-3xl font-bold tracking-wider ${
                      tvTheme === 'light' ? 'text-blue-700' : 'text-white'
                    }`}>WAITING LIST</div>
                  </div>
                  
                  {/* Logo Display */}
                  <div className="flex-1 flex justify-center">
                    {currentRoom?.logoUrl ? (
                      <img
                        src={currentRoom.logoUrl}
                        alt="Room logo"
                        className="object-contain"
                        style={{ height: `${LOGO_SIZES[logoSize].height}px` }}
                      />
                    ) : (
                      <div className={`text-sm text-center ${
                        tvTheme === 'light' ? 'text-gray-600' : 'text-blue-300'
                      }`}>
                        Your logo will appear here
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-xl font-bold ${
                      tvTheme === 'light' ? 'text-gray-900' : 'text-blue-200'
                    }`}>
                      {currentRoom?.name || 'Poker Room'}
                    </div>
                    <div className={`text-sm mt-1 ${
                      tvTheme === 'light' ? 'text-gray-600' : 'text-blue-300'
                    }`}>
                      {new Date().toLocaleDateString('en-US', { 
                        month: '2-digit', 
                        day: '2-digit', 
                        year: '2-digit' 
                      })}
                    </div>
                    <div className="flex items-center justify-end mt-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                      <div className={`text-xs ${
                        tvTheme === 'light' ? 'text-gray-700' : 'text-green-300'
                      }`}>LIVE</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo Size Controls */}
              {currentRoom?.logoUrl && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Logo Size</h3>
                  <div className="flex items-center space-x-2">
                    {Object.entries(LOGO_SIZES).map(([size, config]) => (
                      <button
                        key={size}
                        onClick={() => updateLogoSize(size as LogoSize)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          logoSize === size
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Current height: {LOGO_SIZES[logoSize].height}px
                  </p>
                </div>
              )}

              {/* TV Dashboard Theme Controls */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">TV Dashboard Theme</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateTheme('light')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      tvTheme === 'light'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Light
                  </button>
                  <button
                    onClick={() => updateTheme('dark')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      tvTheme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Dark
                  </button>
                </div>
              </div>

              {/* Scrolling Message Section */}
              <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Scrolling Message</h2>
                
                <div className="mb-6">
                  <label htmlFor="message-text" className="block text-sm font-medium text-gray-700 mb-2">
                    Message Text
                  </label>
                  <textarea
                    id="message-text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Enter your message here... (e.g., Happy Hour 5-7pm • Daily Tournaments at 8pm)"
                    rows={3}
                    maxLength={200}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {messageText.length}/200 characters
                  </p>
                </div>

                {/* Text Size Controls */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Text Size</h3>
                  <div className="flex items-center space-x-2">
                    {Object.entries(MESSAGE_SIZES).map(([size, config]) => (
                      <button
                        key={size}
                        onClick={() => setMessageSize(size as MessageSize)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          messageSize === size
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scroll Speed Controls */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Scroll Speed</h3>
                  <div className="flex items-center space-x-2">
                    {Object.entries(MESSAGE_SPEEDS).map(([speed, config]) => (
                      <button
                        key={speed}
                        onClick={() => setMessageSpeed(speed as MessageSpeed)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          messageSpeed === speed
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={updateMessage}
                    className="btn btn-primary"
                  >
                    Save Message
                  </button>
                  
                  {messageText && (
                    <button
                      onClick={clearMessage}
                      className="btn btn-secondary"
                    >
                      Clear Message
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
        
        {/* Toast Notification */}
        {showToast && (
          <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">Message saved successfully!</span>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
